'use client';

import { ApolloProvider as BaseApolloProvider } from "@apollo/client";
import { createApolloClient } from "./next-client";
import { useMemo } from "react";

interface ApolloWrapperProps {
  children: React.ReactNode;
  siteId: string;
  showError?: (message: string) => void;
  bridgeId?: number;
  bridgeType?: number;
  language?: string;
  override?: string;
  jsonHeaders?: Record<string, string>;
  getAuthToken?: () => string | null;
  // BE-only service-area dimension → `X-Service-Area` (traffic attribution T9).
  // A getter (the area is read per request; see next-client's CreateClientConfig).
  // Must be a STABLE reference (a module-level fn or a useCallback) — it is a memo
  // dependency, so a new function each render would recreate the client.
  getServiceAreaId?: () => string | undefined;
  // BE-only "Hosted at" hint → the framing page's own URL (see next-client's
  // CreateClientConfig). A getter, read per request; must be a STABLE reference (memo
  // dependency below), like getServiceAreaId.
  getHostPageParentUrl?: () => string | undefined;
  onAuthFailure?: () => void;
}

export default function ApolloWrapper({ children, siteId, showError, bridgeId, bridgeType, language, override, jsonHeaders, getAuthToken, getServiceAreaId, getHostPageParentUrl, onAuthFailure }: ApolloWrapperProps) {
  // Serialize jsonHeaders for stable dependency comparison
  const jsonHeadersKey = JSON.stringify(jsonHeaders);

  const client = useMemo(() => {
    const apolloClient = createApolloClient({ siteId, showError, bridgeId, bridgeType, language, override, jsonHeaders, getAuthToken, getServiceAreaId, getHostPageParentUrl, onAuthFailure });

    // Set global reference for legacy code that expects it
    if (typeof window !== 'undefined' && global.window) {
      global.window.apolloClient = apolloClient;
    }

    return apolloClient;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, showError, bridgeId, bridgeType, language, override, jsonHeadersKey, getAuthToken, getServiceAreaId, getHostPageParentUrl, onAuthFailure]);

  return (
    <BaseApolloProvider client={client}>
      {children}
    </BaseApolloProvider>
  );
}
